import { CircularProgress } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Ofis konversiyasi — menejerlar bo'yicha: davrda ofisga kelgan uchrashuvlar, gaplashilgan
 * (noyob) lidlar va ulardan shartnoma tuzganlar; konversiya = shartnomalar / lidlar.
 * data: [{name, meetings, leads, contracts, rate}], total: {meetings, leads, contracts, rate}
 */
const OfficeConversion = ({ data = [], total, isLoading, onOpenLeads = null }) => {
  const { t } = useTranslation();

  // Raqam bosilganda — asosidagi lidlar (kind: meetings | leads | contracts)
  const open = (row, kind) => {
    if (!onOpenLeads) return;
    let leads;
    let suffix;
    if (kind === 'contracts_no_visit') {
      // Ofisga kelmasdan shartnoma qilganlar (davr ichida)
      leads = row?.no_visit_list || [];
      suffix = t('boss.crm.conversion.noVisitContracts');
    } else {
      const list = row?.leads_list || [];
      leads = kind === 'contracts' ? list.filter((l) => l?.bought) : list;
      suffix =
        kind === 'contracts'
          ? t('boss.crm.conversion.contracts')
          : t('boss.crm.conversion.talkedLeads');
    }
    onOpenLeads(`${row?.name || t('common.table.total')} — ${suffix}`, leads);
  };

  // "6/1" — ofisga kelib shartnoma qilganlar / kelmasdan qilganlar
  const contractsCell = (row) => (
    <>
      {cell(row, 'contracts', row?.contracts)}
      {(row?.contracts_no_visit || 0) > 0 && (
        <>
          <span className='text-gray-400 mx-0.5'>/</span>
          {cell(row, 'contracts_no_visit', row?.contracts_no_visit, 'text-amber-600')}
        </>
      )}
    </>
  );
  const cell = (row, kind, value, extra = '') => (
    <button
      type='button'
      onClick={() => open(row, kind)}
      className={`underline decoration-dotted underline-offset-4 hover:text-[#0057FF] ${extra}`}
    >
      {value}
    </button>
  );

  const rateColor = (rate) =>
    rate >= 30 ? 'bg-emerald-500' : rate >= 10 ? 'bg-amber-400' : 'bg-red-400';

  return (
    <div className='residential-complex-item total-statistics'>
      <div className='residential-complex-item-header'>
        <div className='residential-complex-item-title total-statistics-title text-lg font-semibold'>
          {t('boss.crm.conversion.office')}
        </div>
        <div className='text-sm text-gray-500'>
          {t('boss.crm.conversion.officeHint')} · {t('boss.crm.conversion.slashHint')}
        </div>
      </div>

      {isLoading ? (
        <div className='h-[200px] flex items-center justify-center'>
          <CircularProgress size={30} color='inherit' />
        </div>
      ) : data.length === 0 ? (
        <div className='text-gray-400 text-sm text-center py-8'>
          {t('boss.crm.conversion.noOffice')}
        </div>
      ) : (
        <div className='overflow-x-auto my-scroll mt-3'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='text-left text-gray-500 border-b border-gray-100'>
                <th className='py-2 pr-4 font-medium'>{t('boss.crm.conversion.manager')}</th>
                <th className='py-2 pr-4 font-medium text-right'>{t('boss.crm.conversion.meetings')}</th>
                <th className='py-2 pr-4 font-medium text-right'>{t('boss.crm.conversion.talkedLeads')}</th>
                <th className='py-2 pr-4 font-medium text-right'>{t('boss.crm.conversion.contracts')}</th>
                <th className='py-2 font-medium min-w-[200px]'>{t('boss.crm.conversion.rate')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row?.user_id ?? row?.name} className='border-b border-gray-50 hover:bg-gray-50'>
                  <td className='py-2.5 pr-4 font-medium text-[#464255]'>{row?.name}</td>
                  <td className='py-2.5 pr-4 text-right'>{cell(row, 'meetings', row?.meetings)}</td>
                  <td className='py-2.5 pr-4 text-right'>{cell(row, 'leads', row?.leads)}</td>
                  <td className='py-2.5 pr-4 text-right font-semibold text-emerald-700'>{contractsCell(row)}</td>
                  <td className='py-2.5'>
                    <div className='flex items-center gap-2'>
                      <div className='flex-1 h-2 rounded-full bg-gray-100 overflow-hidden'>
                        <div
                          className={`h-full rounded-full ${rateColor(row?.rate || 0)}`}
                          style={{ width: `${Math.min(100, row?.rate || 0)}%` }}
                        />
                      </div>
                      <span className='w-28 text-right font-medium whitespace-nowrap'>
                        {Number(row?.rate || 0).toFixed(1)}%
                        {(row?.contracts_no_visit || 0) > 0 && (
                          <span className='text-amber-600'>
                            {' / '}
                            {Number(row?.rate_no_visit || 0).toFixed(1)}%
                          </span>
                        )}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
              {total && (
                <tr className='bg-gray-50 font-semibold'>
                  <td className='py-2.5 pr-4'>{t('common.table.total')}</td>
                  <td className='py-2.5 pr-4 text-right'>{cell(total, 'meetings', total?.meetings)}</td>
                  <td className='py-2.5 pr-4 text-right'>{cell(total, 'leads', total?.leads)}</td>
                  <td className='py-2.5 pr-4 text-right text-emerald-700'>{contractsCell(total)}</td>
                  <td className='py-2.5 whitespace-nowrap'>
                    {Number(total?.rate || 0).toFixed(1)}%
                    {(total?.contracts_no_visit || 0) > 0 && (
                      <span className='text-amber-600'>
                        {' / '}
                        {Number(total?.rate_no_visit || 0).toFixed(1)}%
                      </span>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default memo(OfficeConversion);
